import { Address } from './address';
import { HomeDelivery } from './home-delivery';
import { Transport } from './transport';

/*
 * "TourStop" : {
 *   "required" : ["type", "status"],
 *   "type" : "object",
 *   "properties" : {
 *     "type" : {
 *       "description" : "Art des Tour-Stops (Leitcode oder Tür zu Tür Auftrag)",
 *       "enum" : ["TransportCode", "HomeDeliveryContract"],
 *       "type" : "string"
 *     },
 *     "status" : {
 *       "description" : "Status des Tour-Stops. Steuert, ob und wie der Tour-Stop hervorgehoben wird.",
 *       "enum" : ["Default", "Highlight", "Error"],
 *       "type" : "string"
 *     },
 *     "transportCode" : {
 *       "$ref" : "#/definitions/TransportCodeData",
 *       "description" : "Leitcode-Daten (nur bei Art Leitcode befüllt)"
 *     },
 *     "homeDeliveryContract" : {
 *       "$ref" : "#/definitions/HomeDeliveryContractData",
 *       "description" : "Tür zur Tür Auftragsdaten (nur bei Art Tür zu Tür Auftrag befüllt)"
 *     }
 *   }
 * },
 */

type TourStopKind = 'Transport' | 'HomeDelivery';

// Custom enum with strings as values instead of integers
// eslint-disable-next-line no-underscore-dangle,id-blacklist,id-match
const TourStopKind = {
  Transport: 'TransportCode',
  HomeDelivery: 'HomeDeliveryContract',
  TransportCode: 'Transport',
  HomeDeliveryContract: 'HomeDelivery',
};

type TourStopStatus = 'Default' | 'Highlight' | 'Error';

// eslint-disable-next-line no-underscore-dangle, id-blacklist, id-match
const TourStopStatus = {
  Default: 'Default',
  Highlight: 'Highlight',
  Error: 'Error',
};

export class TourStop {
  public isFinished = false;

  constructor(
    public kind: TourStopKind,
    public status: TourStopStatus,
    public transport?: Transport,
    public homeDelivery?: HomeDelivery,
  ) { }

  public static fromJSON(json: {
    // eslint-disable-next-line
    type: string,
    status: string,
    transportCode: any,
    homeDeliveryContract: any,
  }): TourStop {
    if (!json) {
      throw new Error('TourStop JSON should not be undefined');
    }
    if (!json.type) {
      throw new Error('TourStop JSON should have a type proprty');
    }
    if (!json.status) {
      throw new Error('TourStop JSON should have a status proprty');
    }
    if (json.type === TourStopKind.HomeDelivery ? !json.homeDeliveryContract : false) {
      throw new Error('TourStop JSON should have property: homeDeliveryContract if it\'s type is home delivery');
    }
    if (json.type === TourStopKind.Transport ? !json.transportCode : false) {
      throw new Error('TourStop JSON should have property: transportCode if it\'s type is transport');
    }

    return new TourStop(
      TourStopKind[json.type],
      TourStopStatus[json.status],
      json.transportCode ? Transport.fromJSON(json.transportCode) : undefined,
      json.homeDeliveryContract ? HomeDelivery.fromJSON(json.homeDeliveryContract) : undefined,
      // TODO: batch scan possible should be available here, on the home delivery
    );
  }

  // ===== Convenience getters ===== //

  /**
   * @return true if this is a "Gruppengepäck" stop, false otherwise
   */
  public get isBatchScanPossible(): boolean {
    // batch scanning is currently only supported on home delivery
    return this.isHomeDelivery && this.homeDelivery?.isBatchScanPossible;
  }

  public get isHomeDelivery(): boolean {
    return this.kind === TourStopKind[TourStopKind.HomeDelivery];
  }

  public get isTransport(): boolean {
    return this.kind === TourStopKind[TourStopKind.Transport];
  }

  public get timeFrame(): string {
    return this.isHomeDelivery
      ? this.homeDelivery.origin.timeFrame || this.homeDelivery.destination.timeFrame
      : undefined;
  }

  public get associatedName(): string {
    return this.isHomeDelivery ? this.homeDelivery.associatedName : undefined;
  }

  public get address(): Address {
    return this.isHomeDelivery ? this.homeDelivery.address : this.transport.address;
  }

  public get isHighlighted(): boolean {
    return this.status === TourStopStatus.Highlight;
  }

  public get hasError(): boolean {
    return this.status === TourStopStatus.Error;
  }

  public get isOnDemandTransport(): boolean {
    return this.isTransport && this.transport.isOnDemand;
  }

  public get isStarted(): boolean {
    if (this.isTransport) {
      return this.transport.started;
    } else {
      return false;
    }
  }

  // ===== Public operations ===== //

  public markAsStarted(): void {
    if (this.isTransport) {
      this.transport.started = true;
    }
  }
}
